import React from "react";
import { useShallow } from "zustand/shallow";
import {
  MainPanelParameterKey,
  parameterRelatedAlerts,
} from "../../data/parameterAlerts";
import { useStore } from "../../store";
import {
  CurrentAction,
  selectMHRadius,
  selectSWL,
} from "../../store/slices/craneSlice";
import { countAlertsForDefinitions } from "../../utils/alertHelpers";
import { BoomIndicator } from "../ui/BoomIndicator";
import { IconButton } from "../ui/IconButton";
import { LayoutSelector } from "../ui/LayoutSelector";
import { LoadCurveChart } from "../ui/LoadCurveChart";
import { SlewIndicator } from "../ui/SlewIndicator";

const NotificationBadge = ({
  variant,
  amount,
}: {
  variant: "error" | "warning";
  amount?: number;
}) => {
  return (
    <span
      className={`min-w-2 min-h-2 max-w-4  rounded-full text-crane-dark text-label-sm p-1 flex items-center leading-none aspect-square justify-center ${
        variant === "error" ? "bg-crane-accent" : "bg-crane-amber"
      }`}
    >
      {amount}
    </span>
  );
};

export const MainPanel: React.FC = () => {
  const { craneParameters, showLayoutSelector, openLayoutSelector } =
    useStore();
  const alerts = useStore((s) => s.alerts);
  const currentAction = useStore((s) => s.currentAction);

  const iconsForActions: Record<CurrentAction, string> = {
    Hoisting: "/assets/icon-hoisting.png",
    Lowering: "/assets/icon-lowering.png",
    "Booming up": "/assets/icon-booming-up.png",
    "Booming down": "/assets/icon-booming-down.png",
    "Slewing cw": "/assets/icon-slewing-cw.png",
    "Slewing ccw": "/assets/icon-slewing-ccw.png",
    Idle: "/assets/icon-idle.png",
  };

  const swl = useStore(useShallow(selectSWL));

  const mhRadius = useStore(useShallow(selectMHRadius));

  const getAlertColorForParameter = (parameter: MainPanelParameterKey) => {
    const { error, warning } = countAlertsForDefinitions(
      alerts,
      parameterRelatedAlerts[parameter]
    );
    return error > 0
      ? "text-crane-accent"
      : warning > 0
      ? "text-crane-amber"
      : "text-crane-white";
  };

  if (showLayoutSelector) {
    return (
      <div className="w-full h-full flex-col flex bg-crane-dark">
        <LayoutSelector />
      </div>
    );
  }

  const allWarningsCount = (
    Object.keys(parameterRelatedAlerts) as MainPanelParameterKey[]
  )
    .map((param) =>
      countAlertsForDefinitions(alerts, parameterRelatedAlerts[param])
    )
    .reduce((acc, curr) => acc + curr.warning, 0);

  const allErrorsCount = (
    Object.keys(parameterRelatedAlerts) as MainPanelParameterKey[]
  )
    .map((param) =>
      countAlertsForDefinitions(alerts, parameterRelatedAlerts[param])
    )
    .reduce((acc, curr) => acc + curr.error, 0);

  return (
    <div className={`w-full h-full flex-col flex`}>
      {/* Left content */}
      <div className="mb-2">
        {/* Header */}
        <div className="flex justify-between flex-1">
          <div className="flex items-center space-x-3 ">
            {/* <ArrowUpRightIcon className="w-6 h-6 text-green-400" /> */}
            <img
              src={iconsForActions[currentAction]}
              alt={currentAction}
              className="w-6 h-6"
            />
            <div className="flex items-baseline space-x-3">
              <h2 className="crane-panel-value">{currentAction}</h2>
              <span className="text-crane-gray">Main hoist</span>
              {allWarningsCount > 0 && (
                <NotificationBadge
                  variant="warning"
                  amount={allWarningsCount}
                />
              )}
              {allErrorsCount > 0 && (
                <NotificationBadge variant="error" amount={allErrorsCount} />
              )}
            </div>
          </div>
          <IconButton
            onClick={openLayoutSelector}
            src="/assets/icon-layout.png"
            alt="Layout"
            className=""
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-1 grid-rows-3 min-h-0">
        {/* Vertical load bar */}
        <div className="col-span-[10px] row-span-3 flex flex-col items-center">
          <div className="h-full w-3 bg-crane-slate rounded-full relative">
            <div
              className="absolute bottom-0 w-full bg-white rounded-full transition-all duration-75"
              style={{
                height: `${Math.min(
                  100,
                  (craneParameters.mhLoad / swl) * 100
                )}%`,
              }}
            />
          </div>
        </div>

        {/* SWL */}
        <div className="col-span-4 row-span-1 flex flex-col">
          <div className="crane-panel-label">SWL</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["swl"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "swl"
                    )}`}
                  >
                    {swl.toFixed(1)}
                  </div>
                  <span className="text-crane-gray text-sm">mt</span>
                </>
              );
            })()}
          </div>
        </div>

        {/* Speed section */}
        <div className="col-span-7 row-span-1 flex flex-row space-x-4 items-start">
          <div className="flex items-start flex-col ">
            <div className="crane-panel-label">Speed</div>
            <div className="flex items-center justify-start space-x-2">
              {(() => {
                const { error, warning } = countAlertsForDefinitions(
                  alerts,
                  parameterRelatedAlerts["speed"]
                );
                return (
                  <>
                    {error > 0 ? (
                      <NotificationBadge variant="error" />
                    ) : (
                      warning > 0 && <NotificationBadge variant="warning" />
                    )}
                    <div
                      className={`crane-panel-value ${getAlertColorForParameter(
                        "speed"
                      )}`}
                    >
                      {craneParameters.speed.toFixed(1)}
                    </div>
                    <span className="text-crane-gray text-sm">%</span>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex-1 pt-8">
            {/* Horizontal speed bar */}
            <div className="w-full h-4 bg-crane-slate rounded-full relative mb-4">
              {/* Center line */}
              <div className="absolute left-1/2 top-0 h-full w-0.5 bg-crane-darker" />
              {/* Speed indicator */}
              <div
                className="absolute top-0 h-full bg-crane-selection-green transition-all"
                style={{
                  left: `${50 - Math.abs(Math.min(0, craneParameters.speed))}%`,
                  width: `${Math.abs(craneParameters.speed)}%`,
                }}
              />
              <div
                className="absolute top-0 h-full w-1 bg-red-500 rounded-full"
                style={{
                  left: `${50 + craneParameters.speedMax}%`,
                  transform: "translateX(-50%)",
                }}
              />
              <div
                className="absolute top-0 h-full w-1 bg-red-500 rounded-full"
                style={{
                  left: `${50 + craneParameters.speedMin}%`,
                  transform: "translateX(-50%)",
                }}
              />
              {/* Scale markers */}
              <div className="absolute -bottom-4 left-0 text-xs text-crane-gray">
                -100
              </div>
              <div className="absolute -bottom-4 left-1/2 text-xs transform -translate-x-1/2">
                0
              </div>
              <div className="absolute -bottom-4 right-0 text-xs text-crane-gray">
                100
              </div>
            </div>
          </div>
        </div>

        {/* MH load */}
        <div className="col-span-4 row-span-1 flex flex-col justify-center">
          <div className="crane-panel-label">MH load</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["mhLoad"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value-large ${getAlertColorForParameter(
                      "mhLoad"
                    )}`}
                  >
                    {craneParameters.mhLoad.toFixed(1)}
                  </div>
                  <span className="text-crane-gray text-sm">mt</span>
                </>
              );
            })()}
          </div>
        </div>

        {/* MH radius */}
        <div className="col-span-7 row-span-1 grid grid-cols-subgrid grid-rows-subgrid items-center">
          <div className="col-span-7 row-span-1 items-end grid grid-cols-subgrid grid-rows-subgrid">
            <div className="col-span-3 row-span-1 flex flex-col justify-center">
              <div className="crane-panel-label">MH radius</div>
              <div className="flex items-center justify-start space-x-2">
                {(() => {
                  const { error, warning } = countAlertsForDefinitions(
                    alerts,
                    parameterRelatedAlerts["mhRadius"]
                  );
                  return (
                    <>
                      {error > 0 ? (
                        <NotificationBadge variant="error" />
                      ) : (
                        warning > 0 && <NotificationBadge variant="warning" />
                      )}
                      <div
                        className={`crane-panel-value-large leading-none ${getAlertColorForParameter(
                          "mhRadius"
                        )}`}
                      >
                        {mhRadius.toFixed(1)}
                      </div>
                      <span className="text-crane-gray text-sm">m</span>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Max radius */}
            <div className="col-span-4 row-span-1 flex flex-col justify-end">
              <div className="crane-panel-label">Max. radius</div>
              <div className="flex items-center justify-start space-x-2">
                {(() => {
                  const { error, warning } = countAlertsForDefinitions(
                    alerts,
                    parameterRelatedAlerts["maxRadius"]
                  );
                  return (
                    <>
                      {error > 0 ? (
                        <NotificationBadge variant="error" />
                      ) : (
                        warning > 0 && <NotificationBadge variant="warning" />
                      )}
                      <div
                        className={`crane-panel-value leading-none ${getAlertColorForParameter(
                          "maxRadius"
                        )}`}
                      >
                        {craneParameters.maxRadius}
                      </div>
                      <span className="text-crane-gray text-sm">m</span>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Empty space */}
        {/* <div className="col-span-2 row-span-1"></div> */}

        {/* SWL percentage */}
        <div className="col-span-4 row-span-1 flex flex-col justify-end">
          <div className="crane-panel-label">SWL</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["swlPercentage"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "swlPercentage"
                    )}`}
                  >
                    {((craneParameters.mhLoad / swl) * 100).toFixed(1)}
                  </div>
                  <span className="text-crane-gray text-sm">%</span>
                </>
              );
            })()}
          </div>
        </div>

        {/* Block speed */}
        <div className="col-span-3 row-span-1 flex flex-col justify-end">
          <div className="crane-panel-label">Block position</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["blockPosition"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "blockPosition"
                    )}`}
                  >
                    {craneParameters.blockPosition}
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Abs travel */}
        <div className="col-span-2 row-span-1 flex flex-col justify-end">
          <div className="crane-panel-label">Abs. travel</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["absTravel"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "absTravel"
                    )}`}
                  >
                    {craneParameters.absTravel.toFixed(1)}
                  </div>
                  <span className="text-crane-gray text-sm">m</span>
                </>
              );
            })()}
          </div>
        </div>

        {/* Rel travel */}
        <div className="col-span-2 row-span-1 flex flex-col justify-end">
          <div className="crane-panel-label">Rel. travel</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["relTravel"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "relTravel"
                    )}`}
                  >
                    {craneParameters.relTravel.toFixed(1)}
                  </div>
                  <span className="text-crane-gray text-sm">m</span>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="border-b border-crane-darker my-4" />

      <div className="flex-1 grid grid-cols-12 gap-1 grid-rows-3 min-h-0">
        {/* Load curve */}
        <div className="col-span-8 row-span-3 flex flex-col">
          <div className="mb-3">
            <div className="text-lg font-medium">Load curve</div>
            <div className="text-crane-light-gray text-sm">
              Load, radius, boom angle
            </div>
          </div>
          <div className="flex-1 ">
            <div className="h-full w-full flex items-center justify-center">
              <LoadCurveChart
                className="h-full w-full"
                angle={craneParameters.boomAngle}
                radius={mhRadius}
                load={craneParameters.mhLoad}
              />
            </div>
          </div>
        </div>

        {/* Empty space */}
        <div className="col-span-2 row-span-3"></div>

        {/* Wind speed */}
        <div className="col-span-2 row-span-1">
          <div className="text-crane-gray text-sm flex items-center space-x-2">
            <span>Wind speed</span>
          </div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["windSpeed"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                </>
              );
            })()}
            <div
              className={`crane-panel-value ${getAlertColorForParameter(
                "windSpeed"
              )}`}
            >
              {craneParameters.windSpeed}
            </div>
            <span className="text-crane-gray text-sm">m/s</span>
          </div>
          <img
            src="/assets/wind.svg"
            alt="Wind direction compass"
            className="h-20"
          />
        </div>

        {/* Slew angle */}
        <div className="col-span-2 row-span-1">
          <div className="text-crane-gray text-sm">Slew angle</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const counts = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["slewAngle"]
              );
              return (
                <>
                  {counts.error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    counts.warning > 0 && (
                      <NotificationBadge variant="warning" />
                    )
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "slewAngle"
                    )}`}
                  >
                    {Math.round(craneParameters.slewAngle * 10) / 10}º
                  </div>
                </>
              );
            })()}
          </div>
          <SlewIndicator angle={craneParameters.slewAngle} />
        </div>

        {/* Boom angle */}
        <div className="col-span-2 row-span-1">
          <div className="text-crane-gray text-sm">Boom angle</div>
          <div className="flex items-center justify-start space-x-2">
            {(() => {
              const { error, warning } = countAlertsForDefinitions(
                alerts,
                parameterRelatedAlerts["boomAngle"]
              );
              return (
                <>
                  {error > 0 ? (
                    <NotificationBadge variant="error" />
                  ) : (
                    warning > 0 && <NotificationBadge variant="warning" />
                  )}
                  <div
                    className={`crane-panel-value ${getAlertColorForParameter(
                      "boomAngle"
                    )}`}
                  >
                    {craneParameters.boomAngle.toFixed(1)}º
                  </div>
                </>
              );
            })()}
          </div>
          <BoomIndicator angle={craneParameters.boomAngle} />
        </div>
      </div>
    </div>
  );
};
